export interface SEOMetadata {
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  canonical?: string;
}

export interface SEOConfig {
  defaultTitle: string;
  titleTemplate: string;
  defaultDescription: string;
  defaultKeywords: string[];
  siteUrl: string;
  defaultOgImage: string;
  pages: {
    [key: string]: SEOMetadata;
  };
}

export const seoConfig: SEOConfig = {
  defaultTitle: 'Behandlung.de - Ihr Wegweiser zu Gesundheit und medizinischer Hilfe',
  titleTemplate: '%s | Behandlung.de',
  defaultDescription: 'Finden Sie qualifizierte Gesundheitsdienstleister, informieren Sie sich über Krankheiten, Symptome und Behandlungsmöglichkeiten auf Deutschlands vertrauenswürdiger Gesundheitsplattform.',
  defaultKeywords: [
    'Gesundheit',
    'Arzt finden',
    'Krankheiten',
    'Symptome',
    'Behandlung',
    'Gesundheitsberatung',
    'Medizin',
    'Therapie'
  ],
  siteUrl: 'https://behandlung.de',
  defaultOgImage: '/image.png',

  pages: {
    '/': {
      title: 'Behandlung.de - Ihr Wegweiser zu Gesundheit und medizinischer Hilfe',
      description: 'Finden Sie qualifizierte Gesundheitsdienstleister in Ihrer Nähe. Informieren Sie sich über Krankheiten, Symptome und moderne Behandlungsmöglichkeiten. Ihre Gesundheit in guten Händen.',
      keywords: [
        'Gesundheitsplattform',
        'Arzt finden',
        'Gesundheitsdienstleister',
        'medizinische Beratung',
        'Gesundheitsinformationen'
      ]
    },

    '/anbieter': {
      title: 'Qualifizierte Gesundheitsdienstleister finden',
      description: 'Durchsuchen Sie unser umfangreiches Verzeichnis qualifizierter Gesundheitsdienstleister. Finden Sie Ärzte, Therapeuten und Berater in Ihrer Nähe mit ausführlichen Profilen, Bewertungen und Kontaktinformationen.',
      keywords: [
        'Arztverzeichnis',
        'Therapeuten finden',
        'Gesundheitsdienstleister',
        'Ärzte in der Nähe',
        'Facharzt suchen'
      ]
    },

    '/symptome': {
      title: 'Symptome verstehen - Umfassender Symptom-Ratgeber',
      description: 'Informieren Sie sich über häufige und seltene Symptome. Verstehen Sie mögliche Ursachen, wann Sie einen Arzt aufsuchen sollten und welche Behandlungsmöglichkeiten es gibt.',
      keywords: [
        'Symptome',
        'Krankheitssymptome',
        'Symptom-Ratgeber',
        'Gesundheitssymptome',
        'medizinische Symptome'
      ]
    },

    '/krankheiten': {
      title: 'Krankheiten von A-Z - Umfassendes Krankheitslexikon',
      description: 'Detaillierte Informationen zu Krankheiten, deren Symptomen, Diagnose und Behandlungsmöglichkeiten. Wissenschaftlich fundiert und verständlich erklärt von medizinischen Experten.',
      keywords: [
        'Krankheiten',
        'Krankheitslexikon',
        'Diagnose',
        'Therapie',
        'medizinische Informationen',
        'Gesundheitswissen'
      ]
    },

    '/beratung': {
      title: 'Gesundheitsberatung - Professionelle Unterstützung finden',
      description: 'Finden Sie professionelle Beratung zu verschiedenen Gesundheitsthemen. Von Ernährungsberatung über psychologische Unterstützung bis hin zu spezialisierter medizinischer Beratung.',
      keywords: [
        'Gesundheitsberatung',
        'medizinische Beratung',
        'Ernährungsberatung',
        'psychologische Beratung',
        'Beratungsstellen'
      ]
    },

    '/wissen': {
      title: 'Gesundheitswissen - Expertenbeiträge & aktuelle Artikel',
      description: 'Aktuelle Artikel, Fachbeiträge und News aus der Welt der Medizin und Gesundheit. Von Experten verfasst, wissenschaftlich fundiert und verständlich aufbereitet.',
      keywords: [
        'Gesundheitsblog',
        'medizinische Artikel',
        'Gesundheitsnews',
        'Fachbeiträge',
        'Gesundheitswissen',
        'Expertenbeiträge'
      ]
    },

    '/impressum': {
      title: 'Impressum',
      description: 'Rechtliche Informationen und Kontaktdaten von Behandlung.de. Angaben gemäß § 5 TMG.',
      keywords: ['Impressum', 'Kontakt', 'rechtliche Informationen']
    },

    '/datenschutz': {
      title: 'Datenschutzerklärung',
      description: 'Erfahren Sie, wie wir Ihre persönlichen Daten schützen und verarbeiten. Umfassende Informationen zu Datenschutz und Datensicherheit auf Behandlung.de.',
      keywords: ['Datenschutz', 'Datensicherheit', 'DSGVO', 'Privatsphäre', 'Datenschutzerklärung']
    },

    '/agb': {
      title: 'Allgemeine Geschäftsbedingungen',
      description: 'Allgemeine Geschäftsbedingungen für die Nutzung von Behandlung.de. Rechte und Pflichten für Nutzer und Gesundheitsdienstleister.',
      keywords: ['AGB', 'Nutzungsbedingungen', 'Geschäftsbedingungen', 'Rechte', 'Pflichten']
    },

    '/haftungsausschluss': {
      title: 'Haftungsausschluss',
      description: 'Haftungsausschluss und rechtliche Hinweise zur Nutzung von Behandlung.de. Informationen zu Gewährleistung und Haftung.',
      keywords: ['Haftungsausschluss', 'rechtliche Hinweise', 'Haftung', 'Gewährleistung']
    },

    '/contact': {
      title: 'Kontakt - Wir sind für Sie da',
      description: 'Nehmen Sie Kontakt mit uns auf. Wir beantworten gerne Ihre Fragen zu Behandlung.de und unterstützen Sie bei der Nutzung unserer Plattform.',
      keywords: ['Kontakt', 'Kontaktformular', 'Support', 'Anfrage', 'Hilfe']
    },

    '/register': {
      title: 'Als Gesundheitsdienstleister registrieren',
      description: 'Registrieren Sie sich als Gesundheitsdienstleister auf Behandlung.de und präsentieren Sie Ihre Praxis oder Dienstleistung potenziellen Patienten.',
      keywords: ['Registrierung', 'Anbieter werden', 'Praxis eintragen', 'Profil erstellen']
    },

    '/premium-benefits': {
      title: 'Premium-Vorteile für Gesundheitsdienstleister',
      description: 'Entdecken Sie die Vorteile einer Premium-Mitgliedschaft auf Behandlung.de. Mehr Sichtbarkeit, erweiterte Funktionen und professionelle Präsentation Ihrer Dienstleistungen.',
      keywords: ['Premium', 'Vorteile', 'Mitgliedschaft', 'erweiterte Funktionen', 'Sichtbarkeit']
    },

    '/provider-login': {
      title: 'Anbieter Login',
      description: 'Login-Bereich für registrierte Gesundheitsdienstleister. Verwalten Sie Ihr Profil, beantworten Sie Anfragen und aktualisieren Sie Ihre Informationen.',
      keywords: ['Login', 'Anbieter', 'Zugang', 'Dashboard']
    },

    '/newsletter-success': {
      title: 'Newsletter-Anmeldung erfolgreich',
      description: 'Vielen Dank für Ihre Newsletter-Anmeldung. Sie erhalten regelmäßig Gesundheitstipps und News von Behandlung.de.',
      keywords: ['Newsletter', 'Anmeldung', 'Gesundheitstipps']
    },

    '/newsletter-error': {
      title: 'Newsletter-Anmeldung fehlgeschlagen',
      description: 'Bei der Newsletter-Anmeldung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      keywords: ['Newsletter', 'Fehler']
    }
  }
};

export const getSEOConfig = (pathname: string): SEOMetadata => {
  const pageConfig = seoConfig.pages[pathname];

  if (pageConfig) {
    return {
      ...pageConfig,
      canonical: `${seoConfig.siteUrl}${pathname}`
    };
  }

  return {
    title: seoConfig.defaultTitle,
    description: seoConfig.defaultDescription,
    keywords: seoConfig.defaultKeywords,
    ogImage: seoConfig.defaultOgImage,
    canonical: `${seoConfig.siteUrl}${pathname}`
  };
};
